import { supabase } from '../lib/supabase.js';

export class ClothingItemsManager {
  constructor() {
    this.clothingItems = [];
    this.filteredClothingItems = [];
    this.selectedMainCategory = '';
    this.isLoading = false;
    this.editingItem = null;
    
    // Category mappings for display
    this.categoryMappings = {
      men: {
        title: "Men's Clothing",
        categories: {
          'men_formal_wear': 'Formal Wear',
          'men_casual_wear': 'Casual Wear',
          'men_ethnic_cultural_wear': 'Ethnic & Cultural Wear',
          'men_outerwear': 'Outerwear',
          'men_seasonal_layering': 'Seasonal & Layering'
        }
      },
      women: {
        title: "Women's Clothing",
        categories: {
          'women_tops_blouses': 'Tops & Blouses',
          'women_bottoms': 'Bottoms',
          'women_dresses': 'Dresses',
          'women_ethnic_cultural_wear': 'Ethnic & Cultural Wear',
          'women_outerwear': 'Outerwear',
          'women_other_items': 'Other Items'
        }
      },
      household: {
        title: "Household Items",
        categories: {
          'household_bedding': 'Bedding',
          'household_table_linens': 'Table Linens',
          'household_window_treatments': 'Window Treatments',
          'household_bath_linens': 'Bath Linens',
          'household_miscellaneous': 'Miscellaneous'
        }
      }
    };
  }

  render() {
    return `
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-xl font-bold text-gray-900">Clothing Items Management</h2>
            <p class="text-gray-600">Manage pricing and availability of clothing items</p>
          </div>
          <div class="flex space-x-3">
            <button 
              id="add-item-btn" 
              class="bg-violet-600 text-white px-4 py-2 rounded-md hover:bg-violet-700"
            >
              Add Item
            </button>
            <button 
              id="refresh-items-btn" 
              class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
            >
              Refresh
            </button>
          </div>
        </div>

        <!-- Main Category Filter -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Filter by Main Category
          </label>
          <select 
            id="main-category-filter" 
            class="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="">All Categories</option>
            <option value="men">Men's Clothing</option>
            <option value="women">Women's Clothing</option>
            <option value="household">Household Items</option>
          </select>
        </div>


        <!-- Clothing Items Display -->
        <div id="clothing-items-container">
          <div class="text-center py-8 text-gray-500">
            Loading clothing items...
          </div>
        </div>

        <!-- Modal for Add/Edit Item Form -->
        <div id="item-modal" class="hidden fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div class="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div class="mt-3">
              <h3 class="text-lg font-medium text-gray-900 mb-4" id="modal-form-title">Add New Item</h3>
              
              <form id="item-form-element" class="space-y-4">
                <input type="hidden" id="item-id">
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Item Name
                    </label>
                    <input 
                      type="text" 
                      id="item-name" 
                      required
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="e.g., Dress shirts"
                    >
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select 
                      id="item-category" 
                      required
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="">Select category...</option>
                      ${this.renderCategoryOptions()}
                    </select>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Base Price ($)
                    </label>
                    <input 
                      type="number" 
                      id="item-price" 
                      required
                      step="0.01"
                      min="0"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="0.00"
                    >
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select 
                      id="item-active" 
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
                
                <!-- Message Display Area -->
                <div id="item-form-message" class="hidden mb-4 p-3 rounded-md">
                  <!-- Success/error messages will be displayed here -->
                </div>
                
                <div class="flex space-x-3">
                  <button 
                    type="submit" 
                    class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    Save Item
                  </button>
                  <button 
                    type="button" 
                    id="cancel-form-btn"
                    class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderCategoryOptions() {
    let options = '';
    
    Object.entries(this.categoryMappings).forEach(([genderKey, genderData]) => {
      options += `<optgroup label="${genderData.title}">`;
      Object.entries(genderData.categories).forEach(([categoryKey, categoryLabel]) => {
        options += `<option value="${categoryKey}">${categoryLabel}</option>`;
      });
      options += '</optgroup>';
    });
    
    return options;
  }

  async bindEvents() {
    await this.loadClothingItems();

    // Add item button
    document.getElementById('add-item-btn').addEventListener('click', () => {
      this.showForm();
    });

    // Refresh items button
    document.getElementById('refresh-items-btn').addEventListener('click', () => {
      this.loadClothingItems();
    });

    // Cancel form button
    document.getElementById('cancel-form-btn').addEventListener('click', () => {
      this.hideForm();
    });

    // Form submission
    document.getElementById('item-form-element').addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.saveItem();
    });

    // Close modal when clicking outside
    document.getElementById('item-modal').addEventListener('click', (e) => {
      if (e.target.id === 'item-modal') {
        this.hideForm();
      }
    });

    // Main category filter
    document.getElementById('main-category-filter').addEventListener('change', (e) => {
      this.selectedMainCategory = e.target.value;
      this.filterItemsByMainCategory();
      this.renderClothingItems();
    });
  }

  async loadClothingItems() {
    this.isLoading = true;
    this.showLoadingState();

    try {
      const { data: items, error } = await supabase
        .from('clothing_items')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;

      this.clothingItems = items || [];
      console.log('Loaded clothing items:', this.clothingItems);
      this.filterItemsByMainCategory();
      this.renderClothingItems();
    } catch (error) {
      this.showError('Error loading clothing items: ' + error.message);
    } finally {
      this.isLoading = false;
    }
  }

  filterItemsByMainCategory() {
    if (!this.selectedMainCategory) {
      this.filteredClothingItems = [...this.clothingItems];
    } else {
      this.filteredClothingItems = this.clothingItems.filter(item => {
        const genderKey = this.getGenderFromCategory(item.category);
        return genderKey === this.selectedMainCategory;
      });
    }
    console.log('Filtered clothing items before grouping:', this.filteredClothingItems);
  }

  showLoadingState() {
    const container = document.getElementById('clothing-items-container');
    container.innerHTML = `
      <div class="text-center py-8 text-gray-500">
        <div class="flex items-center justify-center">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
          Loading clothing items...
        </div>
      </div>
    `;
  }

  renderClothingItems() {
    const container = document.getElementById('clothing-items-container');
    
    if (this.filteredClothingItems.length === 0) {
      const message = this.selectedMainCategory 
        ? `No items found in ${this.categoryMappings[this.selectedMainCategory]?.title || 'selected category'}`
        : 'No clothing items found. Click "Add Item" to create your first item.';
      
      container.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          ${message}
        </div>
      `;
      return;
    }

    // Group items by gender and category
    const groupedItems = this.groupItemsByCategory();
    console.log('Grouped items for rendering:', groupedItems);
    
    container.innerHTML = Object.entries(groupedItems).map(([genderKey, genderData]) => {
      if (Object.keys(genderData.items).length === 0) return '';
      
      return `
        <div class="mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
            ${genderData.title}
          </h3>
          
          ${Object.entries(genderData.items).map(([categoryKey, items]) => `
            <div class="mb-6">
              <h4 class="font-medium text-gray-800 mb-3">
                ${this.categoryMappings[genderKey].categories[categoryKey]}
                <span class="text-sm text-gray-500 font-normal">(${items.length} items)</span>
              </h4>
              
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                ${items.map(item => this.renderItemCard(item)).join('')}
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }).join('');

    // Bind item events
    this.bindItemEvents();
  }

  groupItemsByCategory() {
    const grouped = {};
    
    // Initialize structure
    Object.entries(this.categoryMappings).forEach(([genderKey, genderData]) => {
      grouped[genderKey] = {
        title: genderData.title,
        items: {}
      };
    });

    // Group items
    this.filteredClothingItems.forEach(item => {
      const genderKey = this.getGenderFromCategory(item.category);
      if (genderKey && grouped[genderKey]) {
        if (!grouped[genderKey].items[item.category]) {
          grouped[genderKey].items[item.category] = [];
        }
        grouped[genderKey].items[item.category].push(item);
      }
    });

    return grouped;
  }

  getGenderFromCategory(category) {
    if (category.startsWith('men_')) return 'men';
    if (category.startsWith('women_')) return 'women';
    if (category.startsWith('household_')) return 'household';
    return null;
  }

  renderItemCard(item) {
    const statusColor = item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    const statusText = item.is_active ? 'Active' : 'Inactive';
    
    return `
      <div class="border border-gray-200 rounded-md p-4 hover:shadow-md transition-shadow ${!item.is_active ? 'opacity-60' : ''}">
        <div class="flex justify-between items-start mb-2">
          <h5 class="font-medium text-gray-900 text-sm">${item.name}</h5>
          <span class="text-xs px-2 py-1 rounded-full ${statusColor}">
            ${statusText}
          </span>
        </div>
        
        <div class="text-lg font-bold text-blue-600 mb-3">
          $${parseFloat(item.base_price).toFixed(2)}
        </div>
        
        <div class="flex space-x-2">
          <button 
            class="edit-item-btn text-blue-600 hover:text-blue-800 text-sm"
            data-item-id="${item.id}"
          >
            Edit
          </button>
          <button 
            class="toggle-item-btn text-${item.is_active ? 'yellow' : 'green'}-600 hover:text-${item.is_active ? 'yellow' : 'green'}-800 text-sm"
            data-item-id="${item.id}"
            data-current-status="${item.is_active}"
          >
            ${item.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button 
            class="delete-item-btn text-red-600 hover:text-red-800 text-sm"
            data-item-id="${item.id}"
          >
            Delete
          </button>
        </div>
      </div>
    `;
  }

  bindItemEvents() {
    // Edit buttons
    document.querySelectorAll('.edit-item-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const itemId = e.target.dataset.itemId;
        this.editItem(itemId);
      });
    });

    // Toggle status buttons
    document.querySelectorAll('.toggle-item-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const itemId = e.target.dataset.itemId;
        const currentStatus = e.target.dataset.currentStatus === 'true';
        this.toggleItemStatus(itemId, !currentStatus);
      });
    });

    // Delete buttons
    document.querySelectorAll('.delete-item-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const itemId = e.target.dataset.itemId;
        this.deleteItem(itemId);
      });
    });
  }

  showForm(item = null) {
    const modal = document.getElementById('item-modal');
    const title = document.getElementById('modal-form-title');
    
    this.hideMessages();
    
    modal.classList.remove('hidden');
    
    if (item) {
      title.textContent = 'Edit Item';
      this.populateForm(item);
    } else {
      title.textContent = 'Add New Item';
      this.clearForm();
    }
  }

  hideForm() {
    document.getElementById('item-modal').classList.add('hidden');
    this.clearForm();
    this.hideMessages();
    this.editingItem = null;
  }

  populateForm(item) {
    document.getElementById('item-id').value = item.id;
    document.getElementById('item-name').value = item.name;
    document.getElementById('item-category').value = item.category;
    document.getElementById('item-price').value = item.base_price;
    document.getElementById('item-active').value = item.is_active.toString();
    this.editingItem = item;
  }

  clearForm() {
    document.getElementById('item-form-element').reset();
    document.getElementById('item-id').value = '';
    this.hideMessages();
  }

  async saveItem() {
    this.hideMessages();
    
    const itemData = {
      name: document.getElementById('item-name').value,
      category: document.getElementById('item-category').value,
      base_price: parseFloat(document.getElementById('item-price').value),
      is_active: document.getElementById('item-active').value === 'true'
    };

    try {
      const itemId = document.getElementById('item-id').value;
      
      if (itemId) {
        // Update existing item
        const { error } = await supabase
          .from('clothing_items')
          .update(itemData)
          .eq('id', itemId);

        if (error) throw error;
        this.showSuccess('Item updated successfully! Price changes have been saved.');
      } else {
        // Create new item
        const { error } = await supabase
          .from('clothing_items')
          .insert([itemData]);

        if (error) throw error;
        this.showSuccess('Item created successfully! New item is now available.');
      }

      await this.loadClothingItems();
      this.hideForm();
    } catch (error) {
      this.showError('Error saving item: ' + error.message);
    }
  }

  editItem(itemId) {
    const item = this.clothingItems.find(i => i.id === itemId);
    console.log('Attempting to edit item with ID:', itemId, 'Found item:', item);
    if (item) {
      this.showForm(item);
    } else {
      console.error('Item not found for ID:', itemId);
      this.showError('Item not found. Please refresh the page and try again.');
    }
  }

  async toggleItemStatus(itemId, newStatus) {
    try {
      const { error } = await supabase
        .from('clothing_items')
        .update({ is_active: newStatus })
        .eq('id', itemId);

      if (error) throw error;

      await this.loadClothingItems();
      this.showSuccess(`Item ${newStatus ? 'activated' : 'deactivated'} successfully! Status change has been applied.`);
    } catch (error) {
      this.showError('Error updating item status: ' + error.message);
    }
  }

  async deleteItem(itemId) {
    if (!confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('clothing_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      await this.loadClothingItems();
      this.showSuccess('Item deleted successfully! The item has been removed from the system.');
    } catch (error) {
      this.showError('Error deleting item: ' + error.message);
    }
  }

  hideMessages() {
    const messageDiv = document.getElementById('item-form-message');
    if (messageDiv) {
      messageDiv.classList.add('hidden');
      messageDiv.textContent = '';
      messageDiv.className = 'hidden mb-4 p-3 rounded-md';
    }
  }

  showError(message) {
    const messageDiv = document.getElementById('item-form-message');
    if (messageDiv) {
      messageDiv.textContent = message;
      messageDiv.className = 'mb-4 p-3 rounded-md bg-red-50 text-red-800 border border-red-200';
      messageDiv.classList.remove('hidden');
    } else {
      // Fallback to container display if form message div doesn't exist
      const container = document.getElementById('clothing-items-container');
      container.innerHTML = `
        <div class="text-center py-8 text-red-500">
          ${message}
        </div>
      `;
    }
  }

  showSuccess(message) {
    const messageDiv = document.getElementById('item-form-message');
    if (messageDiv) {
      messageDiv.textContent = message;
      messageDiv.className = 'mb-4 p-3 rounded-md bg-green-50 text-green-800 border border-green-200';
      messageDiv.classList.remove('hidden');
      
      // Auto-hide success messages after 5 seconds
      setTimeout(() => {
        this.hideMessages();
      }, 5000);
    } else {
      // Fallback to console log if message div doesn't exist
      console.log('Success:', message);
    }
  }
}